import StandardButton from '@components/ui/buttons/standard-button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import styles, { redText } from './styles';

interface DisclaimerDialogProps {
  open: boolean;
  onAgree: () => void;
}

const DisclaimerDialog = ({ open, onAgree }: DisclaimerDialogProps) => {
  const [agreed, setAgreed] = useState(false);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  const checkIfScrolledToBottom = (el: HTMLDivElement) => {
    const threshold = 4;

    // If content doesn't overflow, allow immediately
    if (el.scrollHeight <= el.clientHeight) {
      setHasReachedBottom(true);
      return;
    }

    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

    if (isAtBottom) {
      setHasReachedBottom(true);
    }
  };

  useEffect(() => {
    if (!open) return;

    setAgreed(false);
    setHasReachedBottom(false);

    const el = scrollAreaRef.current;
    if (!el) return;

    el.scrollTop = 0;

    // Initial check (handles short content)
    checkIfScrolledToBottom(el);
  }, [open]);

  return (
    <Dialog
      open={open}
      maxWidth={false}
      PaperProps={{
        sx: styles.dialogPaper
      }}>
      <Box sx={styles.noticeBox}>
        <ErrorOutlineIcon sx={styles.noticeIcon} />
        <Typography sx={styles.noticeText}>Important Notice</Typography>
      </Box>

      <DialogContent sx={styles.dialogContent}>
        <Box
          ref={scrollAreaRef}
          onScroll={(e) => checkIfScrolledToBottom(e.currentTarget)}
          sx={styles.scrollArea}>
          <Box component="p" sx={styles.textContainer}>
            <p>
              Submitting <span style={redText}>fake</span> or{' '}
              <span style={redText}>forged educational documents</span> will lead to strict action
              against the applicant.
            </p>
            <p>
              All documents uploaded on this portal will undergo{' '}
              <span style={redText}>strict verification</span>.
            </p>
            <p>
              Where fraudulent documents are identified, the matter will be reported to the{' '}
              <span style={redText}>concerned authorities as per applicable regulations</span>.
            </p>
            <p>
              Applicants are required to submit only genuine and authentic educational documents.
              Submission of{' '}
              <span style={redText}>
                fake, forged, or altered documents will lead to rejection of the verification
                request
              </span>{' '}
              and{' '}
              <span style={redText}>
                result in the candidate being restricted from future verification services
              </span>
              .
            </p>
            <p style={{ fontStyle: 'italic', marginBottom: 0 }}>
              Applicants are strongly advised to ensure the accuracy and authenticity of all
              documents submitted.
            </p>
          </Box>
        </Box>

        <Divider sx={styles.divider} />

        <FormControlLabel
          disabled={!hasReachedBottom}
          control={
            <Checkbox
              checked={agreed}
              disabled={!hasReachedBottom}
              onChange={(e) => setAgreed(e.target.checked)}
              sx={styles.checkbox}
            />
          }
          label={
            <Typography
              sx={{
                ...styles.checkboxLabelText,
                color: hasReachedBottom ? '#000000' : '#8CA3BB'
              }}>
              I certify that all uploaded documents are 100% authentic, and I acknowledge that any
              detected forgery will be documented, leading to potential legal action.
            </Typography>
          }
          sx={styles.checkboxLabel}
        />

        <DialogActions sx={styles.dialogActions}>
          <StandardButton
            fullWidth
            variant="contained"
            disabled={!agreed}
            color={agreed ? '#FFFFFF' : '#8CA3BB'}
            backgroundColor={agreed ? '#044DA1' : '#E1E9F2'}
            onClick={onAgree}
            sx={styles.agreeButton}>
            I Agree
          </StandardButton>
        </DialogActions>

        <Typography sx={styles.footerText}>{'Scroll to the bottom to proceed'}</Typography>
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerDialog;
